import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DotSphereSpinner } from '@/components/ui/DotSphereSpinner'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { SendHorizontal, FileCode, Trash2, Download, Loader2, ArrowLeft, Eye, Code2, Save, Copy, Check, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore, useUser } from '@/app/store/auth'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { iacApi, type IaCContext, type IaCPlacement, type IaCProject, type IaCFile } from '../api'
import { useObject } from '@/features/objects/hooks'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'
import { DeploymentPanel } from '../components/DeploymentPanel'
import { IaCOverviewFlow } from '../components/IaCOverviewFlow'
import { ArchitectureFlow } from '../components/architecture/ArchitectureFlow'
import '@/features/ai-chat/core/styles/ChatMessageArea.css'
import '@/features/ai-chat/core/styles/rich-markdown.css'
import { appLocale } from '@/lib/dateUtils'

// Constrain code blocks and pre elements within the chat panel
const iacStyles = document.createElement('style')
iacStyles.textContent = `
.iac-chat .message-bubble { max-width: 100%; overflow: hidden; font-size: 13px; line-height: 1.5; }
.iac-chat .message-bubble p { font-size: 13px; margin-bottom: 4px; }
.iac-chat .message-bubble li { font-size: 13px; }
.iac-chat .message-bubble h1 { font-size: 16px; }
.iac-chat .message-bubble h2 { font-size: 15px; }
.iac-chat .message-bubble h3 { font-size: 14px; }
.iac-chat .message-bubble pre { max-width: 100%; overflow-x: auto; }
.iac-chat .message-bubble .code-block-wrapper { max-width: 100%; overflow: hidden; }
.iac-chat .message-bubble .code-block { max-width: 100%; overflow-x: auto; font-size: 11px; }
.iac-chat .message-bubble table { font-size: 12px; }
.iac-chat .message-bubble img { max-width: 100%; }
.iac-chat .message-bubble a { text-decoration: none; }
.iac-chat .message-bubble u { text-decoration: none; }
.iac-chat .message-sender { font-size: 11px; }
.iac-chat .message-container { padding: 6px 12px; }
`
if (!document.getElementById('iac-styles')) { iacStyles.id = 'iac-styles'; document.head.appendChild(iacStyles) }

interface ChatMessage {
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp?: string
    codeBlocks?: Array<{ filename: string; code: string; language: string }>
}

// marked v17 escapes code/codespan text in its *renderer*, not its tokenizer, so a
// custom renderer that replaces it must escape too. Without this, angle-bracketed
// content (<Directory>, JSX, <script>) is parsed as real HTML and then stripped by
// DOMPurify. NOTE: marked.setOptions is global, so these renderers also serve the
// chat panel (ChatMessageArea) whenever IaCPage mounts after it.
const escapeHtml = (text: unknown) => String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

function setupMarked() {
    const renderer = new marked.Renderer()
    renderer.code = function (token: any) {
        const code = typeof token === 'string' ? token : (token.text || '')
        const lang = (typeof token === 'string' ? '' : token.lang || '').toLowerCase()
        const hide = !lang || lang === 'text' || lang === 'plain' || lang === 'code'
        const safeCode = (typeof token === 'object' && token.escaped) ? code : escapeHtml(code)
        return `<div class="code-block-wrapper"><${hide ? '' : `div class="code-block-header">${escapeHtml(lang)}</div><`}pre class="code-block"><code>${safeCode}</code></pre></div>`
    }
    renderer.codespan = function (token: any) {
        return `<code class="inline-code">${escapeHtml(typeof token === 'string' ? token : (token.text || ''))}</code>`
    }
    renderer.listitem = function (token: any) {
        const html = (token.tokens && (this as any).parser) ? (this as any).parser.parse(token.tokens) : (typeof token === 'string' ? token : (token.text || ''))
        return `<li>${html.replace(/^<p>|<\/p>\s*$/g, '').trim()}</li>\n`
    }
    marked.setOptions({ breaks: false, gfm: true, renderer, pedantic: false })
}

function renderMarkdown(content: string): string {
    const lines = content.split('\n')
    const processed: string[] = []
    let inTable = false
    for (const line of lines) {
        const isTableRow = line.trim().startsWith('|') && line.trim().endsWith('|')
        if (isTableRow) { inTable = true; processed.push(line) }
        else if (inTable && line.trim() === '') { continue }
        else { inTable = false; processed.push(line) }
    }
    return DOMPurify.sanitize(marked.parse(processed.join('\n')) as string)
}

function extractOptions(content: string): { cleanContent: string; options: string[]; multiSelect: boolean } {
    // Check for [MULTI-OPTIONS] (multi-select) or [OPTIONS] (single-select)
    const multiMatch = content.match(/\[MULTI-OPTIONS\]\s*(.+?)$/m)
    const singleMatch = content.match(/\[OPTIONS\]\s*(.+?)$/m)
    const match = multiMatch || singleMatch
    const multiSelect = !!multiMatch
    if (!match) return { cleanContent: content, options: [], multiSelect: false }
    // Drop junk options that are only quotes/punctuation (e.g. a stray `"` left by a
    // malformed [OPTIONS] line) so they don't render as a blank button.
    const options = match[1].split('|').map(o => o.trim()).filter(o => o.replace(/["'`]+/g, '').trim().length > 0)
    // Also detect multi-select keywords in options like "compliance", "requirements"
    const isLikelyMulti = multiSelect || options.some(o => ['PCI-DSS', 'SOX', 'HIPAA', 'ISO 27001', 'GDPR'].includes(o))
    const cleanContent = content.replace(/\[(MULTI-)?OPTIONS\]\s*.+$/m, '').trim()
    return { cleanContent, options, multiSelect: isLikelyMulti }
}

// Strip code blocks from content for chat display — they show in the right panel instead
function stripCodeBlocks(content: string): string {
    // Step 1: Replace complete code blocks that have # File: headers
    let cleaned = content.replace(/```\w*\s*\n#\s*(?:File:\s*)?(.+?)\n[\s\S]*?```/g, (_, filename) => `\n✅ **${filename.trim()}** generated\n`)
    // Step 2: Replace complete code blocks with ## headings
    cleaned = cleaned.replace(/```\w*\s*\n##\s*(.+?)\n[\s\S]*?```/g, (_, heading) => `\n✅ **${heading.trim()}** generated\n`)
    // Step 3: Remove ALL remaining complete code blocks (no header — bash, json, anything)
    cleaned = cleaned.replace(/```\w*\n[\s\S]*?```/g, '')
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '')
    // Step 4: Handle partial/in-progress code blocks (streaming — block started but not closed)
    const openBackticks = (cleaned.match(/```/g) || []).length
    if (openBackticks % 2 !== 0) {
        // There's an unclosed code block — strip everything from the opening ``` to end
        const lastOpen = cleaned.lastIndexOf('```')
        const beforeBlock = cleaned.substring(0, lastOpen)
        // Try to extract filename from partial block
        const partial = cleaned.substring(lastOpen)
        const fileMatch = partial.match(/```\w*\s*\n#\s*(?:File:\s*)?(.+?)[\n\r]/)
        const label = fileMatch ? `⏳ Generating **${fileMatch[1].trim()}**...` : '⏳ Generating file...'
        cleaned = beforeBlock + '\n' + label + '\n'
    }
    // Step 5: Strip deployment guide sections and long bash command sequences
    cleaned = cleaned.replace(/#{1,3}\s*(?:Prerequisites|Pre-flight|Deployment Guide|Next Steps|Troubleshooting|File Structure|Destroy)[\s\S]*?(?=\n#{1,3}\s|\n\*\*[A-Z]|$)/gi, '')
    // Step 5b: Strip the setup/credentials block some generations emit as prose — it belongs in
    // setup-aws.sh / DEPLOYMENT-GUIDE.md, not the chat. Bounded to the "Generation Complete"
    // wrap-up (or end) so the summary after it is preserved.
    cleaned = cleaned.replace(/={5,}/g, '')
    cleaned = cleaned.replace(/(?:^|\n)\s*(?:SAVE THESE CREDENTIALS|AWS Credentials \(save|Terraform Backend Configuration|Update VaultSentinel credentials|Delete old key)[\s\S]*?(?=\n\s*#{1,3}\s*Generation Complete|\n\s*Generation Complete|$)/gi, '\n')
    cleaned = cleaned.replace(/^\s*AWS_(?:ACCESS_KEY_ID|SECRET_ACCESS_KEY|DEFAULT_REGION|SESSION_TOKEN)\b.*$/gmi, '')
    cleaned = cleaned.replace(/(?:^|\n)(?:\s*(?:aws|terraform|export|chmod|cd|echo|curl|bash|cat|cp|\.\/)\s.*\n?){3,}/gm, '\n')
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim()
    return cleaned || '*Generating...*'
}

// A heading above a code block is a project file NAME, and a project file name is relative to
// the project. The model writes real system paths above config examples ('# /etc/systemd/system/
// app.service') and paths relative to somewhere else in a multi-module answer ('# ../modules/vpc/
// main.tf'); the backend refuses either, and refuses EVERY file in the project along with it, so
// the chat and verification stay wedged until the file is deleted. Normalise the name HERE, where
// it is invented: resolve it as a path, drop any '..' that would climb out, and keep what is left
// (23 Sep review - stripping only the leading slashes left the '..' case wedging the chat).
// Returns '' when the heading is not a usable name, and the caller auto-names the block instead.
function toProjectFileName(heading: string): string {
    const parts: string[] = []
    for (const part of heading.split('/')) {
        if (part === '' || part === '.') continue
        if (part === '..') { parts.pop(); continue }
        parts.push(part)
    }
    const name = parts.join('/')
    // The first line of a code block is not always a filename - a long '# this script does…'
    // comment lands here too - and a name (or one part of it) longer than the filesystem takes is
    // another name the backend has to refuse. An auto-named file is better than a wedged project.
    const bytes = (s: string) => new TextEncoder().encode(s).length
    if (bytes(name) > 1024 || parts.some(p => bytes(p) > 255)) return ''
    return name
}

// Exported for its test: this is where a model's heading becomes a project file NAME.
export function extractCodeBlocks(content: string): Array<{ filename: string; code: string; language: string }> {
    const blocks: Array<{ filename: string; code: string; language: string }> = []
    // Match code blocks with:
    //   # File: filename.tf
    //   ## Section heading (for DEPLOYMENT-GUIDE sections)
    //   or no header (auto-named)
    const regex = /```(\w*)\s*\n(?:(?:#\s*(?:File:\s*)?|##\s*)(.+?)\n)?([\s\S]*?)```/g
    let match
    while ((match = regex.exec(content)) !== null) {
        const lang = match[1] || 'hcl'
        const header = match[2] ? toProjectFileName(match[2].trim()) : undefined
        // Skip small inline code examples (bash one-liners, command examples)
        const code = match[3].trim()
        if (!header && code.split('\n').length <= 3 && code.length < 200) continue
        const filename = header || `file_${blocks.length + 1}.${lang === 'hcl' || lang === 'terraform' ? 'tf' : lang}`
        blocks.push({ filename, code, language: lang })
    }
    return blocks
}

function formatTime(ts?: string): string {
    if (!ts) return ''
    return new Date(ts).toLocaleTimeString(appLocale(), { hour: '2-digit', minute: '2-digit' })
}

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    return (
        <button className="text-muted-foreground hover:text-foreground transition-colors p-1" title="Copy"
            onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}>
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
    )
}

// Where each object can be rebuilt, as the server decided it (backend/src/lib/iac-placement.js) - the
// same placement the IaC agent is told to follow. Hover a count for the objects in it and why.
const PLACEMENT_ORDER: Array<{ key: IaCPlacement; label: string; tone: string }> = [
    { key: 'cloud', label: 'iac.placementCloud', tone: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900' },
    { key: 'cloud-native', label: 'iac.placementCloudNative', tone: 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950 dark:text-sky-200 dark:border-sky-900' },
    { key: 'ibm-power', label: 'iac.placementIbmPower', tone: 'bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950 dark:text-violet-200 dark:border-violet-900' },
    { key: 'recovery-site', label: 'iac.placementRecoverySite', tone: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900' },
    { key: 'saas', label: 'iac.placementSaas', tone: 'bg-muted text-muted-foreground border-border' },
    { key: 'review', label: 'iac.placementReview', tone: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900' },
]

function PlacementStrip({ ctx, t }: { ctx: IaCContext | undefined; t: (k: string, o?: any) => string }) {
    if (!ctx?.hierarchyData?.length) return null
    const groups = PLACEMENT_ORDER
        .map((g) => ({ ...g, objects: ctx.hierarchyData.filter((o) => o.placement?.placement === g.key) }))
        .filter((g) => g.objects.length > 0)
    if (groups.length === 0) return null
    return (
        <div className="mt-1 flex flex-wrap items-center gap-1" aria-label={t('iac.placementTitle')} data-testid="iac-placement">
            {groups.map((g) => (
                <span
                    key={g.key}
                    className={`rounded border px-1.5 py-0.5 text-[11px] leading-none ${g.tone}`}
                    title={g.objects.map((o) => `${o.objectId}${o.description ? ` - ${o.description}` : ''}: ${o.placement.reason}`).join('\n')}
                >
                    {t(g.label, { count: g.objects.length })}
                </span>
            ))}
        </div>
    )
}

export function IaCPage() {
    const { mainId, scenario: scenarioParam } = useParams<{ mainId: string; scenario: string }>()
    const scenario = scenarioParam ? parseInt(scenarioParam) : 1
    const navigate = useNavigate()
    const { t } = useTranslation()
    const searchParams = new URLSearchParams(window.location.search)
    const projectId = searchParams.get('pid')
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const user = useUser()
    const aiChatPanel = useAiChatPanel()
    const { data: objectData } = useObject(mainId || null)
    const chatRef = useRef<HTMLDivElement>(null)
    const scrolledUp = useRef(false)
    const agentWorkingRef = useRef(false)

    const [input, setInput] = useState('')
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [sending, setSending] = useState(false)
    const [agentWorking, setAgentWorking] = useState(false)
    // Clara assists with deploy/fix iteration (done in the left chat), so it lives
    // on the left — but only becomes available once deployment has started or the
    // user has downloaded the Terraform to deploy themselves.
    const [deployStatus, setDeployStatus] = useState('not_deployed')
    const [downloaded, setDownloaded] = useState(false)
    const claraAvailable = downloaded || (!!deployStatus && deployStatus !== 'not_deployed')
    const [navWarning, setNavWarning] = useState<{ href: string } | null>(null)
    const [statusMsgs, setStatusMsgs] = useState<string[]>([])
    const [sid, setSid] = useState<string | null>(null)
    const [activeIdx, setActiveIdx] = useState<number | null>(null)
    const [files, setFiles] = useState<Array<{ filename: string; code: string; language: string }>>([])
    const [rightTab, setRightTab] = useState<'code' | 'vis'>('code')
    // Architecture overview — derived on demand from the current Terraform (see below).
    const [archData, setArchData] = useState<any | null>(null)
    const [archHash, setArchHash] = useState<string>('')
    const [archBuilding, setArchBuilding] = useState(false)
    const [archError, setArchError] = useState<string | null>(null)
    const projIdRef = useRef<number | null>(null)   // resolved project id for the architecture cache

    // Resizable left/right split — `rightPct` is the right pane's width as a % of the row.
    const [rightPct, setRightPct] = useState(50)
    const splitRef = useRef<HTMLDivElement>(null)
    const startSplitDrag = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const container = splitRef.current
        if (!container) return
        const onMove = (ev: MouseEvent) => {
            const rect = container.getBoundingClientRect()
            const pct = ((rect.right - ev.clientX) / rect.width) * 100
            setRightPct(Math.min(75, Math.max(25, pct)))   // keep both panes usable
        }
        const onUp = () => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
    }, [])

    const tfFilesForArch = useMemo(
        () => files.filter((f) => /\.tf$/i.test(f.filename)).map((f) => ({ filename: f.filename, code: f.code || '' })),
        [files]
    )
    const currentArchHash = useMemo(() => {
        const s = tfFilesForArch.map((f) => `${f.filename}:${f.code}`).join('|')
        let h = 0
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
        return String(h)
    }, [tfFilesForArch])
    const archStale = !!archData && archHash !== currentArchHash

    // `force` regenerates from the Terraform (Rebuild button); otherwise the backend
    // returns the persisted diagram for this project when the Terraform is unchanged.
    const buildArchitecture = useCallback(async (force = false) => {
        if (tfFilesForArch.length === 0) return
        setArchBuilding(true)
        setArchError(null)
        try {
            const res = await iacApi.deriveArchitecture(tfFilesForArch, { projectId: projIdRef.current, force })
            const data = (res.data as any)?.data
            if (data && Array.isArray(data.containers) && Array.isArray(data.nodes)) {
                setArchData(data)
                setArchHash(currentArchHash)
            } else {
                setArchError('Could not derive the architecture from the Terraform.')
            }
        } catch (e: any) {
            setArchError(e?.response?.data?.error || e?.message || 'Failed to build architecture')
        } finally {
            setArchBuilding(false)
        }
    }, [tfFilesForArch, currentArchHash])

    // Auto-derive once when the Overview is opened and there's no diagram yet.
    useEffect(() => {
        if (rightTab === 'vis' && tfFilesForArch.length > 0 && !archData && !archBuilding && !archError) {
            buildArchitecture()
        }
    }, [rightTab, tfFilesForArch.length, archData, archBuilding, archError, buildArchitecture])
    const [loaded, setLoaded] = useState(false)
    const [showRestart, setShowRestart] = useState(false)
    const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())
    const [verified, setVerified] = useState(false)
    // terraform validate result (authoritative once it has run)
    const [validating, setValidating] = useState(false)
    // A silent background self-heal validate (on reload) drives the stepper spinner but
    // NOT the blocking "do not navigate away" banner — that's only for the active verify.
    const [validatingSilent, setValidatingSilent] = useState(false)
    const [validation, setValidation] = useState<{
        ran: boolean; valid: boolean; attempts: number; errorCount: number; warningCount: number;
        diagnostics: Array<{ severity: string; summary: string; detail: string; file: string | null; line: number | null }>;
        changedFiles: string[];
    } | null>(null)
    const validatedSigRef = useRef<string>('')   // signature of the last validated file-set
    const armValidationRef = useRef(false)        // set after a generation turn; gates auto-validate
    // The auto-save that follows each agent answer waits a second. Leaving the page inside that second
    // left the timer to fire on a page that was gone (the pipeline's test run caught it, 1 Oct 2026).
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }, [])
    // Verification status is persisted to project.status ('draft' | 'verified') so it
    // survives a reload — otherwise re-entering a project always shows "awaiting verification".
    const statusHydratedRef = useRef(false)
    const persistedStatusRef = useRef<string | null>(null)
    const restoreValidatedRef = useRef(false)     // gates the one-time re-validate on a restored project
    const genAbortRef = useRef<AbortController | null>(null) // aborts an in-flight generation when leaving a project

    useEffect(() => { setupMarked() }, [])
    useEffect(() => { if (!scrolledUp.current && chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight }, [messages, statusMsgs])

    // Detect verification completion from assistant messages
    useEffect(() => {
        if (verified) return
        const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
        if (lastAssistant?.content && (
            lastAssistant.content.includes('VERIFICATION SUMMARY') ||
            lastAssistant.content.includes('Cross-provider verification result') ||
            lastAssistant.content.includes('verification result: PASS') ||
            lastAssistant.content.includes('Verdict') && lastAssistant.content.includes('PASS')
        )) {
            setVerified(true)
        }
    }, [messages, verified])

    // Run `terraform validate` server-side after a generation turn. The backend
    // auto-fixes validate errors via the model and returns the corrected files,
    // so the user receives already-validated Terraform (fewer deploy iterations).
    const runValidation = useCallback(async (sig: string, opts?: { silent?: boolean }) => {
        setValidating(true)
        setValidatingSilent(!!opts?.silent)
        try {
            const res = await iacApi.validate(files)
            const d: any = res.data
            const corrected = Array.isArray(d.files)
                ? d.files.map((f: any) => ({ filename: f.filename, code: f.code ?? f.content ?? '', language: f.filename?.endsWith('.tf') ? 'hcl' : 'text' }))
                : null
            // Anchor the validated signature to the FINAL file-set so the effect doesn't loop.
            validatedSigRef.current = corrected ? corrected.map((f: any) => `${f.filename}:${(f.code || '').length}`).join('|') : sig
            setValidation({ ran: !!d.ran, valid: !!d.valid, attempts: d.attempts || 0, errorCount: d.errorCount || 0, warningCount: d.warningCount || 0, diagnostics: d.diagnostics || [], changedFiles: d.changedFiles || [] })
            if (d.ran) setVerified(!!d.valid)
            if (corrected && d.changedFiles?.length) {
                setFiles(corrected)
                setTimeout(() => { if (!saveMut.isPending) saveMut.mutate() }, 400)
            }
            // Nothing was checked (terraform missing, or a provider this server does not install).
            // Say so: silence here left the engineer looking at "awaiting verification" with no
            // idea why, and a provider refusal is not a fault in their Terraform.
            if (!d.ran && d.message && !opts?.silent) {
                setMessages(p => [...p, { role: 'assistant', content: `⚠️ **Verification did not run** — ${d.message}`, timestamp: new Date().toISOString() }])
            }
            if (d.ran && !opts?.silent) {
                const errs = (d.diagnostics || []).filter((x: any) => x.severity === 'error')
                const content = d.valid
                    ? (d.changedFiles?.length
                        ? `✅ **terraform validate passed** — auto-fixed ${d.changedFiles.length} issue(s) in ${d.changedFiles.join(', ')}. The corrected files are ready to deploy.`
                        : `✅ **terraform validate passed** — no issues found. Ready to deploy.`)
                    : `⚠️ **terraform validate found ${d.errorCount} error(s)** that need attention:\n\n${errs.slice(0, 10).map((x: any, i: number) => `${i + 1}. ${x.file ? `\`${x.file}${x.line ? `:${x.line}` : ''}\` — ` : ''}${x.summary}`).join('\n')}`
                setMessages(p => [...p, { role: 'assistant', content, timestamp: new Date().toISOString() }])
            }
        } catch (e: any) {
            validatedSigRef.current = sig  // infra error — don't hammer-retry
            // The validate call errored (e.g. the SSE stream closed without a
            // result). Set a defined "did not run" state so the Verify milestone
            // RESOLVES instead of hanging on a silent spinner, and tell the user
            // so they can retry rather than staring at "awaiting verification".
            setValidation({ ran: false, valid: false, attempts: 0, errorCount: 0, warningCount: 0, diagnostics: [], changedFiles: [] })
            if (!opts?.silent) {
                // A refused project file is not a "try again": the file has to be renamed or
                // deleted first, and the server's message names it.
                const refused = e?.code === 'INVALID_PROJECT_FILE'
                const content = refused
                    ? `⚠️ Verification can't run: ${e.message} Rename that file, or delete it with the bin icon above the code, then press **Verify**.`
                    : `⚠️ Verification didn't complete (${e?.message || 'the validator returned no result'}). Your Terraform is generated — press **Verify** to try again.`
                setMessages(p => [...p, { role: 'assistant', content, timestamp: new Date().toISOString() }])
            }
        } finally {
            setValidating(false)
            setValidatingSilent(false)
        }
    }, [files])

    // After a generation turn (armed in send's finally), validate once the agent
    // is idle and the .tf file-set has actually changed.
    useEffect(() => {
        if (agentWorking || validating) return
        if (!armValidationRef.current) return
        const tf = files.filter(f => /\.tf$/i.test(f.filename))
        const sig = files.map(f => `${f.filename}:${(f.code || '').length}`).join('|')
        if (tf.length === 0 || validatedSigRef.current === sig) { armValidationRef.current = false; return }
        armValidationRef.current = false
        // The .tf set changed — the prior verification no longer applies until it re-validates.
        setVerified(false); setValidation(null)
        runValidation(sig)
    }, [agentWorking, files, validating, runValidation])

    // Authoritative "ready to deploy" signal: terraform validate result once it
    // has run, otherwise fall back to the agent's self-reported verification.
    const verifyPassed = validation?.ran ? validation.valid : verified

    // Overview needs Terraform files to diagram — if they're gone (e.g. a new project
    // reset while it was open), fall back to the Code tab so the panel isn't blank.
    useEffect(() => { if (files.length === 0 && rightTab === 'vis') setRightTab('code') }, [files.length, rightTab])

    // Keep ref in sync for event handlers
    useEffect(() => { agentWorkingRef.current = agentWorking }, [agentWorking])

    // Warn user not to navigate away while agent is working
    useEffect(() => {
        if (!agentWorking) return

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }

        // Push a dummy history entry so back button triggers popstate instead of leaving
        window.history.pushState({ iacGuard: true }, '')

        const handlePopState = () => {
            if (agentWorkingRef.current) {
                window.history.pushState({ iacGuard: true }, '')
                setNavWarning({ href: 'back' })
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        window.addEventListener('popstate', handlePopState)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            window.removeEventListener('popstate', handlePopState)
        }
    }, [agentWorking])

    // Intercept sidebar/menu link clicks while agent is working
    useEffect(() => {
        if (!agentWorking) return
        const handler = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('a[href], [data-nav-link]')
            if (!target) return
            const href = target.getAttribute('href')
            if (!href || href === '#') return
            if (href === window.location.pathname) return
            e.preventDefault()
            e.stopPropagation()
            setNavWarning({ href })
        }
        document.addEventListener('click', handler, true)
        return () => document.removeEventListener('click', handler, true)
    }, [agentWorking])


    // Remove container padding/maxWidth to use full screen width (same as visualization page)
    useEffect(() => {
        const main = document.querySelector('main')
        const container = main?.querySelector(':scope > div')
        if (main) { main.style.overflow = 'hidden'; main.style.padding = '0' }
        if (container) { (container as HTMLElement).style.padding = '0'; (container as HTMLElement).style.maxWidth = 'none'; (container as HTMLElement).style.height = '100%' }
        return () => {
            if (main) { main.style.overflow = ''; main.style.padding = '' }
            if (container) { (container as HTMLElement).style.padding = ''; (container as HTMLElement).style.maxWidth = ''; (container as HTMLElement).style.height = '' }
        }
    }, [])

    const { data: ctx, isLoading } = useQuery({
        queryKey: ['iac-context', mainId, scenario],
        queryFn: async () => { const r = await iacApi.getContext(mainId!, scenario); const d = r.data as any; return (d?.data || d) as IaCContext },
        enabled: !!mainId && isAuthenticated,
    })

    const { data: projData } = useQuery({
        queryKey: ['iac-projects', mainId],
        queryFn: async () => { const r = await iacApi.listProjects(mainId!); const d = r.data as any; return (d?.projects || []) as IaCProject[] },
        enabled: !!mainId && isAuthenticated,
    })

    // When a project id is in the URL (?pid=N) it is the source of truth for WHICH project this is.
    // Do NOT fall back to projData[0] while the list is still loading/refetching — that briefly
    // resolves `proj` to a DIFFERENT (old) project, so proj.id flips out from under an in-flight
    // generation (tripping stale() + the reset effect → aborted run, wiped files). Null until the
    // requested project is actually present.
    const proj = projectId
        ? (projData || []).find((p: any) => p.id === Number(projectId)) || null
        : (projData || [])[0] || null

    // Keep the resolved project id available to buildArchitecture (declared earlier).
    // Identity for in-flight work (generation staleness, buildArchitecture, auto-save) is the URL's
    // project id — stable from the first render. proj?.id lags behind it (the projects list refetches
    // after a create), and keying off proj?.id makes a new project's id change mid-generation.
    useEffect(() => { projIdRef.current = projectId ? Number(projectId) : (proj?.id ?? null) }, [projectId, proj?.id])

    // When the selected project actually changes (e.g. create a new project, then navigate
    // into an existing one), the page does NOT remount — so the one-time hydration guards and
    // per-project state would stay stale, leaving the Overview button hidden and the old
    // project's files/diagram on screen. Reset them so the newly selected project hydrates fresh.
    const prevProjIdRef = useRef<number | null>(null)
    useEffect(() => {
        const id = proj?.id ?? null
        if (id == null) return
        if (prevProjIdRef.current == null) { prevProjIdRef.current = id; return } // first project — let normal hydration run
        if (id === prevProjIdRef.current) return
        prevProjIdRef.current = id

        // Abort any generation still streaming for the project we're leaving, and clear the
        // working flags — otherwise its callbacks keep mutating this (completed) project's state.
        genAbortRef.current?.abort()
        setAgentWorking(false)
        setSending(false)
        setStatusMsgs([])
        statusHydratedRef.current = false
        restoreValidatedRef.current = false
        persistedStatusRef.current = null
        validatedSigRef.current = ''
        armValidationRef.current = false
        setVerified(false)
        setValidation(null)
        setArchData(null)
        setArchHash('')
        setActiveIdx(null)
        // Drop the previous project's chat session + conversation so the init effect
        // (gated on `sid`, deps [ctx, sid]) re-runs for the newly-selected project and
        // loads ITS own session/messages. Without this the old project's messages persist
        // and keep the milestones lit (currentIdx jumps to Verify) on a brand-new project.
        setSid(null)
        setMessages([])
        setLoaded(false)
        const ex = (proj?.files || []).map((f: IaCFile) => ({ filename: f.filePath, code: f.content, language: f.filePath.endsWith('.tf') ? 'hcl' : 'text' }))
        setFiles(ex)
    }, [proj?.id])

    useEffect(() => {
        if (proj?.files) {
            const ex = proj.files.map((f: IaCFile) => ({ filename: f.filePath, code: f.content, language: f.filePath.endsWith('.tf') ? 'hcl' : 'text' }))
            if (ex.length > 0 && files.length === 0) setFiles(ex)
        }
    }, [proj])

    // Restore the saved verification state when an existing project loads, so the Verify
    // step stays green and Test Deployment stays enabled across reloads.
    useEffect(() => {
        if (statusHydratedRef.current || !proj) return
        if (proj.status === 'verified') setVerified(true)
        statusHydratedRef.current = true
    }, [proj])

    // Persist verification state to project.status whenever it changes (after hydration).
    // persistedStatusRef is seeded from the loaded status so we don't write redundantly.
    useEffect(() => {
        if (!statusHydratedRef.current || !proj?.id) return
        if (persistedStatusRef.current === null) persistedStatusRef.current = proj.status || 'draft'
        const desired = verifyPassed ? 'verified' : 'draft'
        if (persistedStatusRef.current === desired) return
        persistedStatusRef.current = desired
        iacApi.updateProject(proj.id, { status: desired }).catch(() => { persistedStatusRef.current = null })
    }, [verifyPassed, proj?.id, proj?.status])

    // Self-heal verification on a RESTORED project: a reload doesn't re-arm the auto-validate,
    // so a project that was generated/verified in an earlier session would show "Awaiting
    // verification" forever. If it has generated .tf files but no persisted 'verified' status,
    // re-run terraform validate once (silently — no chat message) so the Verify step and Test
    // Deployment button reflect reality. On pass it persists 'verified', so later loads hydrate.
    useEffect(() => {
        if (restoreValidatedRef.current || !statusHydratedRef.current || !proj) return
        if (proj.status === 'verified') { restoreValidatedRef.current = true; return }
        if (agentWorking || validating || sending || armValidationRef.current) return
        const tf = files.filter(f => /\.tf$/i.test(f.filename))
        if (tf.length === 0) return
        restoreValidatedRef.current = true
        const sig = files.map(f => `${f.filename}:${(f.code || '').length}`).join('|')
        runValidation(sig, { silent: true })
    }, [proj, files, agentWorking, validating, sending, runValidation])

    useEffect(() => {
        if (!ctx || sid) return
        ;(async () => {
            // Read JSON only if the response actually is JSON; otherwise log a useful
            // diagnostic. Without this guard a stale CloudFront 404→/index.html
            // (HTML body, 200 status) throws "Unexpected token '<'" and breaks init.
            const readJsonOrWarn = async (label: string, res: Response): Promise<any | null> => {
                const ct = res.headers.get('content-type') || ''
                if (!ct.toLowerCase().includes('application/json')) {
                    const body = await res.text().catch(() => '')
                    console.warn(`IaC init: ${label} returned non-JSON (status=${res.status}, content-type="${ct}"). Body head:`, body.slice(0, 200))
                    return null
                }
                try { return await res.json() } catch (e) {
                    console.warn(`IaC init: ${label} JSON parse failed`, e)
                    return null
                }
            }

            try {
                const r = await fetch('/api/ai/chat/iac-session', { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ userId: user?.id ? String(user.id) : 'system', mainId, scenario, projectId: projectId || undefined, projectDescription: proj?.description || undefined }) })
                if (!r.ok) {
                    console.warn(`IaC init: iac-session HTTP ${r.status}`)
                    return
                }
                const d = await readJsonOrWarn('iac-session', r)
                if (!d?.sessionId) return
                setSid(d.sessionId)
                try {
                    const h = await fetch(`/api/ai/chat-history/session/${d.sessionId}/messages`, { headers: authHeaders() })
                    if (h.ok) {
                        const hd = await readJsonOrWarn('chat-history/messages', h)
                        const saved = (hd?.messages || []).map((m: any) => ({ role: m.role, content: m.content, timestamp: m.timestamp || m.createdAt, codeBlocks: m.role === 'assistant' ? extractCodeBlocks(m.content || '') : undefined }))
                        // Restore the conversation for DISPLAY only. Do NOT replay every past
                        // message's code blocks back into `files` — the project's saved .tf files
                        // (loaded from the DB by the hydration effect) are the single source of truth
                        // for the CURRENT Terraform. Re-unioning every generation pass's blocks here is
                        // what made the file set grow on each re-entry (42 -> 145 -> 185).
                        if (saved.length > 0) { setMessages(saved); setLoaded(true); return }
                    }
                } catch (e) { console.warn('IaC init: chat-history fetch failed', e) }
                setMessages([{ role: 'assistant', content: `Hierarchy loaded for **${ctx.serviceName}** — ${ctx.objectCount} objects, ${ctx.versionCount} backup versions.\n\nClick **Start Analysis** to begin.`, timestamp: new Date().toISOString() }])
            } catch (e) { console.error('IaC init failed:', e) }
        })()
    }, [ctx, sid])

    const addFiles = useCallback((blocks: Array<{ filename: string; code: string; language: string }>) => {
        setFiles(prev => {
            const u = [...prev]
            blocks.forEach(b => { const i = u.findIndex(f => f.filename === b.filename); i >= 0 ? u[i] = b : u.push(b) })
            // Sync files to session context so Agent SDK can read them
            if (sid && u.length > 0) {
                fetch(`/api/ai/chat/iac-session/${sid}/files`, {
                    method: 'PUT',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({ files: u.map(f => ({ filename: f.filename, code: f.code })) }),
                }).catch(() => {})
            }
            return u
        })
    }, [sid])

    // Remove a file from the right pane. If the project has it saved, delete the saved row too:
    // filtering React state alone brought the file back on the next load, and a file the agent
    // route will not accept (an absolute name from a '# /etc/...' heading) refuses the chat for
    // the whole project until the row is gone (22 Sep review).
    const removeFile = useCallback(async (index: number) => {
        const file = files[index]
        if (!file) return
        setFiles(f => f.filter((_, j) => j !== index))
        setActiveIdx(null)
        armValidationRef.current = true   // file removed — re-validate so Verify reflects the new set
        const pid = proj?.id ?? (projectId ? Number(projectId) : null)
        const saved = (proj?.files || []).some((f: IaCFile) => f.filePath === file.filename)
        if (!pid || !saved) return
        try {
            await iacApi.deleteProjectFile(pid, file.filename)
            queryClient.invalidateQueries({ queryKey: ['iac-projects', mainId] })
        } catch (e: any) {
            toast({ title: 'Could not delete the saved file', description: e?.response?.data?.error || e?.message || file.filename, variant: 'destructive' })
        }
    }, [files, proj, projectId, mainId, queryClient, toast])

    // Use Agent SDK when files exist (deployment/troubleshooting phase)
    // Use standard orchestrator during initial generation (IaC agent needs code output format)
    const useAgentSDK = files.length > 0

    async function send(msg: string) {
        if (!msg.trim() || !sid) return
        // Tie this generation to the current project so we can drop it if the user navigates
        // to a different project before it finishes (otherwise it clobbers the new project's state).
        genAbortRef.current?.abort()
        const ac = new AbortController()
        genAbortRef.current = ac
        const myProjId = projIdRef.current
        const stale = () => ac.signal.aborted || projIdRef.current !== myProjId
        setSending(true)
        setAgentWorking(true)
        setStatusMsgs([])
        scrolledUp.current = false

        // Add user message
        const ts = new Date().toISOString()
        setMessages(p => [...p, { role: 'user', content: msg, timestamp: ts }])
        setInput('')

        // If switching to Agent SDK, sync files first
        if (useAgentSDK && files.length > 0) {
            await fetch(`/api/ai/chat/iac-session/${sid}/files`, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ files: files.map(f => ({ filename: f.filename, code: f.code })) }),
            }).catch(() => {})
        }

        let content = ''
        let addedAssistantMsg = false

        try {
            // Agent SDK for deployment help (reads files autonomously)
            // Standard orchestrator for code generation (IaC agent prompt)
            const endpoint = useAgentSDK ? '/api/ai/agent-sdk/message' : '/api/ai/chat/message'
            const r = await fetch(endpoint, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ sessionId: sid, message: msg }),
                signal: ac.signal,
            })
            if (!r.ok) {
                // Show the server's reason when it gives one - e.g. a project file named outside the
                // project, which the agent route now refuses by name (22 Sep) - not just "HTTP 400".
                const reason = await r.json().then((b) => (typeof b?.error === 'string' ? b.error : null)).catch(() => null)
                throw new Error(reason || `HTTP ${r.status}`)
            }

            const reader = r.body?.getReader()
            const dec = new TextDecoder()
            let buf = ''

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    if (stale()) break // user navigated to another project — stop applying updates
                    buf += dec.decode(value, { stream: true })
                    const evts = buf.split('\n\n')
                    buf = evts.pop() || ''

                    for (const evt of evts) {
                        let type = '', data = ''
                        for (const ln of evt.split('\n')) {
                            if (ln.startsWith('event: ')) type = ln.slice(7).trim()
                            if (ln.startsWith('data: ')) data = ln.slice(6)
                        }
                        if (!data) continue

                        try {
                            const j = JSON.parse(data)
                            if (type === 'status' && j.status) {
                                setStatusMsgs(p => [...p, j.status])
                            } else if (type === 'error' && j.error) {
                                // The server refused or failed mid-stream (e.g. a project file it
                                // cannot contain, named in the message). The loop used to ignore
                                // this event, so the engineer was told 'No response received.'
                                // instead of the reason (22 Sep review).
                                content += `${content ? '\n\n' : ''}Error: ${j.error}`
                            } else if (j.content) {
                                content += j.content

                                // First content chunk: hide spinner, add assistant message
                                if (!addedAssistantMsg) {
                                    addedAssistantMsg = true
                                    setSending(false)
                                    setStatusMsgs([])
                                    setMessages(p => [...p, { role: 'assistant', content, timestamp: new Date().toISOString() }])
                                } else {
                                    // Subsequent chunks: update last message in place
                                    const c = content
                                    // Check for completed code blocks and extract them progressively
                                    const cb = extractCodeBlocks(c)
                                    if (cb.length > 0) addFiles(cb)
                                    setMessages(p => {
                                        const u = [...p]
                                        u[u.length - 1] = { ...u[u.length - 1], content: c, codeBlocks: cb.length > 0 ? cb : u[u.length - 1].codeBlocks }
                                        return u
                                    })
                                }
                            } else if (j.response) {
                                content = j.response
                            }
                        } catch {}
                    }
                }
            }

            // Finalize
            if (stale()) return // navigated away mid-generation — don't write into another project
            if (!content) content = 'No response received.'
            const cb = extractCodeBlocks(content)
            setMessages(p => {
                const u = [...p]
                if (addedAssistantMsg && u.length > 0) {
                    u[u.length - 1] = { ...u[u.length - 1], content, codeBlocks: cb.length > 0 ? cb : undefined }
                } else {
                    u.push({ role: 'assistant', content, timestamp: new Date().toISOString(), codeBlocks: cb.length > 0 ? cb : undefined })
                }
                return u
            })
            if (cb.length > 0) addFiles(cb)
        } catch (e: any) {
            if (e?.name === 'AbortError' || stale()) return // generation aborted by a project switch — drop silently
            setMessages(p => [...p, { role: 'assistant', content: `Error: ${e?.message || 'Failed'}`, timestamp: new Date().toISOString() }])
        } finally {
            // If the user navigated to another project mid-generation, this turn is orphaned:
            // don't clear flags or arm validation for the now-current project (the switch handled that).
            if (!stale()) {
                setSending(false)
                setAgentWorking(false)
                setStatusMsgs([])
                // Arm a terraform-validate pass; the effect runs it only if the .tf
                // file-set actually changed this turn (no-op for plain Q&A turns).
                armValidationRef.current = true
                // Auto-save: persist any generated files after each agent response
                if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
                autoSaveTimerRef.current = setTimeout(() => {
                    autoSaveTimerRef.current = null
                    // Guard the delayed save against a project switch during the 1s wait —
                    // projIdRef must still be the project this generation belonged to, else
                    // we'd persist these files to the wrong (now-current) project.
                    if (projIdRef.current !== myProjId || saveMut.isPending) return
                    setFiles(currentFiles => {
                        if (currentFiles.length > 0) saveMut.mutate()
                        return currentFiles
                    })
                }, 1000)
            }
        }
    }

    const saveMut = useMutation({
        mutationFn: async () => {
            // Prefer the URL's project id so a still-loading `proj` (null) doesn't create a DUPLICATE.
            let pid = proj?.id ?? (projectId ? Number(projectId) : undefined)
            if (!pid) { const r = await iacApi.createProject({ mainId: mainId!, name: `${ctx?.serviceName || mainId} - Terraform`, cloudPlatform: 'multi', scenario }); pid = (r.data as any)?.project?.id }
            if (!pid) throw new Error('No project'); for (const f of files) { if (f.filename && f.code) await iacApi.saveFile(pid, { filePath: f.filename, content: f.code }) }; return pid
        },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['iac-projects', mainId] }); toast({ title: `${files.length} files saved` }) },
        onError: (e: any) => toast({ title: t('iac.saveFailed'), description: e?.message || 'Could not save — check your connection and retry.', variant: 'destructive' }),
    })

    function download() {
        const c = files.map(f => `# File: ${f.filename}\n\n${f.code}\n`).join('\n\n')
        const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([c], { type: 'text/plain' })); a.download = `${mainId}-terraform.tf`; a.click()
    }

    const active = activeIdx !== null ? files[activeIdx] : null

    if (isLoading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>

    return (
        <div className="flex flex-col h-full overflow-hidden" style={{ height: 'calc(100vh - var(--topbar-height, 56px))' }}>
            {/* Page header */}
            <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <h1 className="text-lg font-semibold">{t('iac.title')}</h1>
                        <div className="flex items-center gap-2 mt-1.5">
                            {objectData?.iconFilename && (
                                <EntityIcon src={buildIconPath(objectData.iconFilename)} alt="" className="h-6 w-6" />
                            )}
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold">{mainId}</span>
                                    <span className="text-xs text-muted-foreground">— {ctx?.objectCount || 0} objects, {ctx?.versionCount || 0} versions</span>
                                </div>
                                <PlacementStrip ctx={ctx} t={t} />
                                <p className="text-xs text-muted-foreground">{objectData?.objectDescription || ctx?.serviceName || ''}</p>
                                {proj?.description && (
                                    <p className="text-xs text-blue-600 font-medium">{proj.description}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowRestart(true)}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5 text-blue-600" />New Project
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => saveMut.mutate()} disabled={files.length === 0 || saveMut.isPending}>
                        <Save className="h-3.5 w-3.5 mr-1.5 text-blue-600" />Save Project
                    </Button>
                </div>
            </div>

            {/* Agent working warning. A silent background self-heal validate (reload) shows only
                the subtle stepper spinner above — not this blocking banner. */}
            {(agentWorking || (validating && !validatingSilent)) && (
                <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-800">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <span className="font-medium">{validating && !agentWorking
                        ? t('iac.agentVerifying', 'Verifying the Terraform — do not navigate away from this page.')
                        : t('iac.agentWorking')}</span>
                </div>
            )}

            {/* Panels */}
            <div ref={splitRef} className="flex flex-1 overflow-hidden border-t">
            {/* LEFT: Chat */}
            <div className="iac-chat flex-1 flex flex-col overflow-hidden">
                {/* Phase progress bar */}
                {messages.length > 1 && messages.filter(m => m.role === 'user').length > 0 && (() => {
                    const phases = [
                        { key: 'analyse', label: t('iac.phaseAnalyse', 'Analyse') },
                        { key: 'questions', label: t('iac.phaseQuestions', 'Questions') },
                        { key: 'plan', label: t('iac.phasePlan', 'Plan') },
                        { key: 'generate', label: t('iac.phaseGenerate', 'Generate') },
                        { key: 'verify', label: t('iac.phaseVerify', 'Verify') },
                    ]
                    const assistantMsgs = messages.filter(m => m.role === 'assistant')
                    const hasFiles = files.length > 0
                    const allContent = messages.map(m => m.content).join('\n')
                    const hasVerification = /VERIFICATION|verification|self-verification|cross-provider|SELF-VERIFICATION COMPLETE|sending to independent/i.test(allContent)
                    const hasAskedQuestion = assistantMsgs.some(m => /\[OPTIONS\]|\[MULTI-OPTIONS\]/i.test(m.content))
                    const hasPlanConfirm = messages.some(m => /proceed to generate|Are these settings correct|ready to generate|Yes, proceed|DEPLOYMENT SUMMARY|CONFIGURATION SUMMARY|Let me summarize/i.test(m.content))
                    // "Generating" only once REAL files exist — actual fenced file output
                    // (# File:), the setup script, or the post-strip "** generated" marker.
                    // Prose like "I'll generate the Terraform…" or a bare "TERRAFORM
                    // GENERATION COMPLETE" header (which can appear with no files) must NOT
                    // advance the stepper past Plan.
                    const isGenerating = hasFiles || assistantMsgs.some(m => /# File:|setup-aws\.sh|\*\*\s*\S.*\*\*\s*generated/i.test(m.content))

                    let currentIdx = 0
                    if (!hasAskedQuestion) currentIdx = 0
                    else if (hasAskedQuestion && !hasPlanConfirm && !isGenerating) currentIdx = 1
                    else if (hasPlanConfirm && !isGenerating) currentIdx = 2
                    if (isGenerating) currentIdx = 3
                    // Advance to Verify when terraform validate is running/done, when
                    // verification text appeared, OR when the agent finished generating.
                    // Use agentWorking (not sending) — sending flips false on the first
                    // streamed chunk while files are still being generated.
                    if (validating || validation || hasVerification || (hasFiles && !agentWorking)) currentIdx = 4

                    return (
                        <div className="px-6 py-3 border-b bg-card">
                            <div className="flex items-center">
                                {phases.map((p, i) => {
                                    const isVerifyStep = i === phases.length - 1
                                    // Verify goes green once terraform validate passes — the clear
                                    // "validated / ready to deploy" signal. Gate on currentIdx so a
                                    // verifyPassed restored from a previously-completed project doesn't
                                    // light Verify green during a fresh run that's still back at Analyse.
                                    const isDone = i < currentIdx || (isVerifyStep && verifyPassed && currentIdx === phases.length - 1)
                                    const isCurrent = i === currentIdx && !isDone
                                    // The active step spins whenever work is actually happening
                                    // (agent generating, or terraform validate running), so the
                                    // progress circles are consistent — not only Verify.
                                    const showSpinner = isCurrent && (agentWorking || validating)
                                    return (
                                    <div key={p.key} className="flex items-center" style={{ flex: i < phases.length - 1 ? 1 : 'none' }}>
                                        <div className="flex flex-col items-center" style={{ minWidth: 32 }}>
                                            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-all ${
                                                isDone
                                                    ? 'border-green-500 bg-green-500/20 text-green-700 dark:text-green-400'
                                                    : isCurrent
                                                        ? 'border-blue-500 bg-blue-500/20 text-blue-700 dark:text-blue-400'
                                                        : 'border-muted-foreground/30 bg-muted/50 text-muted-foreground'
                                            }`}>
                                                {isDone ? <Check className="h-4 w-4" /> : showSpinner ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : i + 1}
                                            </div>
                                            <span className={`text-[10px] mt-1 whitespace-nowrap font-medium ${
                                                isDone
                                                    ? 'text-green-700 dark:text-green-400'
                                                    : isCurrent
                                                        ? 'text-blue-700 dark:text-blue-400'
                                                        : 'text-muted-foreground'
                                            }`}>{p.label}</span>
                                        </div>
                                        {i < phases.length - 1 && (
                                            <div className={`flex-1 h-0.5 mx-1 mt-[-14px] transition-all ${
                                                isDone ? 'bg-green-500/40' : 'bg-muted-foreground/20'
                                            }`} />
                                        )}
                                    </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })()}
                {/* Messages */}
                <div ref={chatRef} onScroll={() => { if (!chatRef.current) return; scrolledUp.current = chatRef.current.scrollHeight - chatRef.current.scrollTop - chatRef.current.clientHeight > 100 }}
                    className="chat-message-area" style={{ flex: 1, minHeight: 0 }}>
                    {messages.map((m, i) => {
                        const isLastAssistant = m.role === 'assistant' && i === messages.length - 1 && !sending
                        const { cleanContent, options, multiSelect } = m.role === 'assistant' ? extractOptions(m.content) : { cleanContent: m.content, options: [], multiSelect: false }
                        // Strip code blocks from chat display — they show in the right panel
                        const hasCodeBlocks = m.codeBlocks && m.codeBlocks.length > 0
                        // Always strip code blocks from assistant messages — they show in the Code panel
                        let displayContent = m.role === 'assistant' ? stripCodeBlocks(cleanContent) : cleanContent
                        // The agent titles its generation message "TERRAFORM GENERATION COMPLETE" the
                        // moment it STARTS generating; show accurate wording while files still stream.
                        if (m.role === 'assistant' && isLastAssistant && agentWorking) {
                            displayContent = displayContent.replace(/TERRAFORM GENERATION COMPLETE/gi, 'Generating Terraform')
                        }

                        return (
                        <div key={i} className={`message ${m.role === 'user' ? 'user' : 'agent'} message-container`}>
                            <div className="message-sender">{m.role === 'user' ? t('iac.you') : <><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</>}</div>
                            <div className="message-bubble">
                                <div className="md-rich" dangerouslySetInnerHTML={{ __html: renderMarkdown(displayContent || cleanContent) }} />
                                {/* Files show in right panel — no badges needed in chat */}
                            </div>
                            {/* Option buttons */}
                            {isLastAssistant && options.length > 0 && (
                                <div className="detail-buttons-container" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                                    {multiSelect ? (
                                        <>
                                            {options.map((opt, oi) => {
                                                const isSelected = selectedOptions.has(opt)
                                                return (
                                                    <button key={oi}
                                                        className={`detail-button ${isSelected ? 'more-details-button' : ''}`}
                                                        style={isSelected ? { background: 'hsl(var(--primary) / 0.15)', borderColor: 'hsl(var(--primary))' } : undefined}
                                                        onClick={() => setSelectedOptions(prev => {
                                                            const next = new Set(prev)
                                                            next.has(opt) ? next.delete(opt) : next.add(opt)
                                                            return next
                                                        })}>
                                                        {isSelected && <Check className="h-3 w-3 mr-1 inline" />}{opt}
                                                    </button>
                                                )
                                            })}
                                            <button className="detail-button more-details-button"
                                                style={{ fontWeight: 600 }}
                                                disabled={selectedOptions.size === 0}
                                                onClick={() => { send([...selectedOptions].join(', ')); setSelectedOptions(new Set()) }}>
                                                Confirm Selection →
                                            </button>
                                        </>
                                    ) : (
                                        options.map((opt, oi) => (
                                            <button key={oi} className="detail-button more-details-button" onClick={() => send(opt)}>
                                                {opt}
                                            </button>
                                        ))
                                    )}
                                    {messages.filter(msg => msg.role === 'user').length > 1 && (
                                        <button className="detail-button" style={{ borderColor: 'hsl(var(--muted-foreground))', color: 'hsl(var(--muted-foreground))' }}
                                            onClick={() => send('Go back to the previous question')}>
                                            ← Go Back
                                        </button>
                                    )}
                                </div>
                            )}
                            {/* Go back button when no options */}
                            {isLastAssistant && options.length === 0 && !hasCodeBlocks && messages.filter(msg => msg.role === 'user').length > 1 && (
                                <div className="detail-buttons-container">
                                    <button className="detail-button" style={{ borderColor: 'hsl(var(--muted-foreground))', color: 'hsl(var(--muted-foreground))' }}
                                        onClick={() => send('Go back to the previous question')}>
                                        ← Go Back
                                    </button>
                                </div>
                            )}
                            <div className="message-meta">
                                <span className="message-time">{formatTime(m.timestamp)}</span>
                                {m.role === 'assistant' && <CopyButton text={m.content} />}
                            </div>
                        </div>
                        )
                    })}

                    {messages.length === 1 && messages[0].role === 'assistant' && !sending && !loaded && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                            <Button onClick={() => send('Please analyse this hierarchy and classify each object. Identify all IP addresses from the metadata and classify them as public-facing or internal. Then present your findings for my review.')}>
                                Start Analysis
                            </Button>
                        </div>
                    )}

                    {/* Verification in progress indicator */}
                    {!sending && agentWorking && messages.length > 0 && /verification|cross-provider|sending to independent/i.test(messages[messages.length - 1]?.content || '') && (
                        <div className="message agent message-container">
                            <div className="message-sender"><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</div>
                            <div className="flex items-center gap-3 px-3 py-3">
                                <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium">Independent verification in progress...</p>
                                    <p className="text-xs text-muted-foreground">The terraform-verify agent is reviewing all generated files</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {sending && (
                        <div className="message agent message-container">
                            <div className="message-sender"><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</div>
                            <div className="progress-panel" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                                <div className="progress-spinner">
                                    <DotSphereSpinner size={48} logoSrc="/images/vsl-logo.png" variant="light" />
                                </div>
                                <div className="progress-messages">
                                    {statusMsgs.length > 0 ? statusMsgs.slice(-3).map((s, i) => <div key={i} className="progress-item">{s}</div>) :
                                        <div className="progress-item">Thinking...</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Activate Clara — lives on the left, because deploy/fix iteration
                    happens in this chat. Only available once deployment has started or
                    the user has downloaded the Terraform to deploy themselves. */}
                {files.length > 0 && claraAvailable && (
                    <div className="flex items-center justify-end px-3 pt-2">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                            const platform = files.some(f => f.code.includes('azurerm')) ? 'Azure'
                                : files.some(f => f.code.includes('google_')) ? 'GCP' : 'AWS'
                            const contextSummary = [
                                `IaC Deployment Context: ${ctx?.serviceName || mainId} — ${platform}`,
                                `${files.length} Terraform files generated`,
                                `Objects: ${ctx?.objectCount || 0}, Versions: ${ctx?.versionCount || 0}`,
                                `Key files: ${files.map(f => f.filename).join(', ')}`,
                            ].join('\n')
                            aiChatPanel.open({ tab: 'clara', context: contextSummary })
                        }}>
                            <Eye className="h-3 w-3 mr-1" />Activate Clara
                        </Button>
                    </div>
                )}

                {/* Input */}
                <form onSubmit={e => { e.preventDefault(); send(input) }} className="px-3 py-2 border-t flex items-center gap-2">
                    <Input value={input} onChange={e => setInput(e.target.value)}
                        placeholder={files.length > 0 ? t('iac.askDeployment') : t('iac.askInfrastructure')}
                        disabled={sending || !sid} className="flex-1 rounded-full px-4" />
                    <button type="submit" disabled={sending || !input.trim() || !sid}
                        className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors flex-shrink-0 ${sending || !input.trim() || !sid ? 'opacity-50 cursor-not-allowed text-muted-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground cursor-pointer'}`}>
                        <SendHorizontal className="h-5 w-5" />
                    </button>
                </form>
            </div>

            {/* RIGHT: Terraform files */}
            {/* Drag handle to resize the two panes */}
            <div
                onMouseDown={startSplitDrag}
                className="w-1.5 shrink-0 cursor-col-resize bg-border hover:bg-primary/50 transition-colors"
                role="separator"
                aria-orientation="vertical"
                title="Drag to resize"
            />
            <div className="flex flex-col overflow-hidden shrink-0" style={{ width: `${rightPct}%` }}>
                <Tabs value={rightTab} onValueChange={v => setRightTab(v as any)} className="flex flex-col min-h-0 flex-1">
                    <TabsList className="w-full justify-start border-b rounded-none h-[72px] items-end p-0 bg-transparent">
                        <TabsTrigger value="code" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 gap-1.5">
                            <Code2 className="h-4 w-4 text-blue-600" />Code
                            {files.length > 0 && <Badge variant="secondary" className="text-[9px] ml-1">{files.length}</Badge>}
                        </TabsTrigger>
                        {/* Overview is available as soon as there are Terraform files to diagram.
                            It derives the architecture from the files and is independent of the
                            slower, sometimes-failing terraform-validate step — so a pending or
                            failed Verify never hides the diagram. */}
                        {files.length > 0 && (
                            <TabsTrigger value="vis" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 gap-1.5">
                                <Eye className="h-4 w-4 text-blue-600" />Overview
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="code" className="flex-1 flex overflow-hidden m-0 data-[state=inactive]:hidden">
                        {files.length === 0 ? (
                            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                                <div className="text-center"><FileCode className="h-10 w-10 mx-auto mb-2 opacity-30" /><p>{t('iac.noTerraformFiles')}</p><p className="text-xs mt-1">{t('iac.filesAppear')}</p></div>
                            </div>
                        ) : (
                            <>
                                <div className="w-52 border-r overflow-y-auto py-1">
                                    {files.map((f, i) => (
                                        <button key={i}
                                            className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-left hover:bg-accent/50 transition-colors ${i === activeIdx ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
                                            onClick={() => setActiveIdx(i)}>
                                            <FileCode className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{f.filename}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    {active ? (
                                        <>
                                            <div className="flex items-center justify-between px-3 py-1.5 border-b">
                                                <span className="text-xs font-mono text-muted-foreground">{active.filename}</span>
                                                <div className="flex items-center gap-1">
                                                    <CopyButton text={active.code} />
                                                    <Button variant="ghost" size="sm" className="h-6 px-1" title="Delete file" onClick={() => { if (activeIdx !== null) removeFile(activeIdx) }}>
                                                        <Trash2 className="h-3 w-3 text-muted-foreground" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <pre className="flex-1 overflow-auto p-4 text-xs font-mono leading-relaxed bg-muted/30 text-foreground">
                                                <code>{active.code}</code>
                                            </pre>
                                        </>
                                    ) : (
                                        <div className="flex-1 flex items-center justify-center text-muted-foreground text-xs">Select a file to view</div>
                                    )}
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="vis" className="flex-1 min-h-0 overflow-hidden m-0">
                        {(() => {
                            // Architecture diagram derived on demand from the CURRENT Terraform
                            // (it changes as the engineer requests updates). Module/resource tree
                            // is the loading/fallback state.
                            const tfFiles = files.map((f) => ({ filename: f.filename, code: f.code || '' }))
                            const mdFile = files.find((f) => /\.md$/i.test(f.filename))
                            const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content || ''
                            const overviewMd = mdFile?.code || lastAssistant

                            if (tfFilesForArch.length === 0) {
                                return <IaCOverviewFlow files={tfFiles} markdown={overviewMd} />
                            }
                            if (archData) {
                                return (
                                    <ArchitectureFlow
                                        architecture={archData}
                                        onRebuild={() => buildArchitecture(true)}
                                        rebuilding={archBuilding}
                                        stale={archStale}
                                        context={ctx}
                                        terraform={tfFiles.map((f) => f.code).join('\n')}
                                    />
                                )
                            }
                            if (archBuilding) {
                                return (
                                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('iac.overviewBuilding', 'Building architecture diagram from Terraform…')}
                                    </div>
                                )
                            }
                            // Error or not yet built — show the tree with a build action.
                            return (
                                <div className="relative h-full w-full">
                                    <div className="absolute right-3 top-3 z-10">
                                        <Button variant="outline" size="sm" onClick={() => buildArchitecture(false)} disabled={archBuilding}>
                                            <Eye className="h-4 w-4" />
                                            <span className="ml-1.5">{t('iac.overviewBuild', 'Build architecture diagram')}</span>
                                        </Button>
                                    </div>
                                    {archError && (
                                        <div className="absolute left-3 top-3 z-10 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                                            {archError}
                                        </div>
                                    )}
                                    <IaCOverviewFlow files={tfFiles} markdown={overviewMd} />
                                </div>
                            )
                        })()}
                    </TabsContent>
                </Tabs>

                {/* Deployment panel — always visible at bottom. The files-generated
                    summary sits here on the deployment side. */}
                <div className="border-t px-3 py-2 flex-shrink-0">
                    <DeploymentPanel
                        projectId={proj?.id || null}
                        cloudPlatform={proj?.cloudPlatform || 'aws'}
                        files={files}
                        mainId={mainId || ''}
                        scenario={scenario}
                        hierarchyContext={ctx || null}
                        verified={verifyPassed}
                        onAskAgentToFix={(errorMessage) => {
                            // Send the deployment error to the IaC agent for analysis
                            const fixPrompt = `The Terraform deployment failed with the following error. Please analyse the error, identify the root cause, and regenerate the affected files with the fix applied.\n\n${errorMessage}`
                            send(fixPrompt)
                        }}
                        onStatusChange={setDeployStatus}
                        onDownloaded={() => setDownloaded(true)}
                        busy={agentWorking}
                    />
                </div>
            </div>
            </div>

            {/* Restart confirmation */}
            {showRestart && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-2">Start New Project?</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This will delete the current conversation and all generated Terraform files. This action cannot be undone.
                        </p>
                        <p className="text-sm font-medium mb-4">Do you want to continue?</p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRestart(false)}>No, Cancel</Button>
                            <Button variant="destructive" size="sm" onClick={async () => {
                                setShowRestart(false)
                                try {
                                    // Delete session from backend
                                    if (sid) {
                                        await fetch(`/api/ai/chat-history/session/${sid}`, { method: 'DELETE', headers: authHeaders() }).catch(() => {})
                                    }
                                    // Delete project files
                                    if (proj?.id) {
                                        await iacApi.deleteProject(proj.id).catch(() => {})
                                    }
                                } catch {}
                                // Reset local state
                                setSid(null)
                                setMessages([])
                                setFiles([])
                                setActiveIdx(null)
                                setLoaded(false)
                                queryClient.invalidateQueries({ queryKey: ['iac-projects', mainId] })
                                toast({ title: t('iac.projectReset') })
                            }}>
                                Yes, Delete and Restart
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation warning dialog */}
            <Dialog open={!!navWarning} onOpenChange={(open) => { if (!open) setNavWarning(null) }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('iac.agentWorkingTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('iac.agentWorkingDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setNavWarning(null)}>
                            {t('iac.stayOnPage')}
                        </Button>
                        <Button variant="destructive" onClick={() => {
                            const href = navWarning?.href
                            setNavWarning(null)
                            // Stop the guard SYNCHRONOUSLY. setAgentWorking(false) only updates
                            // agentWorkingRef on a later effect tick, so without this the popstate
                            // fired by navigation below re-runs handlePopState (ref still true) ->
                            // it re-pushes a dummy entry and re-opens this dialog, trapping the
                            // user on the page with a dead back button.
                            agentWorkingRef.current = false
                            setAgentWorking(false)
                            if (href === 'back') {
                                // The guard pushed dummy /iac/<id> history entries, so navigate(-1)
                                // would just land on another same-URL dummy ("doesn't leave"). Leave
                                // deterministically to the IaC list instead.
                                navigate('/iac', { replace: true })
                            } else if (href) {
                                navigate(href)
                            }
                        }}>
                            {t('iac.leavePage')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
